import type { AppSession, MeView, PracticeView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { fmtAgo, fmtTemp, pct } from '../../lib/format';
import { Icon } from '../../components/Mark';
import { Avatar, Card, Empty } from '../../components/Bits';
import { GoalsMet } from '../../components/Goals';

/** Population tiles, open alerts, and the patient table sorted by who needs attention. */
export function Dashboard({ session, practice, onChanged, say }: { session: AppSession; me: MeView; practice: PracticeView; onChanged: () => Promise<void>; say: (s: string) => void }) {
  const open = practice.alerts.filter((a) => !a.ackedAt);
  const ack = async (id: string) => {
    try {
      await api.ackAlert(id, session.token);
      await onChanged();
    } catch (e) {
      say(e instanceof ApiError ? e.message : 'Could not acknowledge that alert.');
    }
  };
  return (
    <>
      <header className="desk-head">
        <div>
          <h1>Patients</h1>
          <span className="hint">
            {practice.name} · {practice.stats.enrolled} enrolled
          </span>
        </div>
        <a className="button primary" href="#/practice/enroll">
          <Icon name="plus" size={16} /> Enroll a patient
        </a>
      </header>
      <div className="stat-grid">
        <Card className="stat-card">
          <span className="hint">Enrolled</span>
          <strong>{practice.stats.enrolled}</strong>
        </Card>
        <Card className="stat-card">
          <span className="hint">Active this week</span>
          <strong>{practice.stats.active7d}</strong>
        </Card>
        <Card className="stat-card">
          <span className="hint">Avg adherence</span>
          <strong>{pct(practice.stats.avgAdherence)}</strong>
        </Card>
        <Card className="stat-card">
          <span className="hint">Logged a temperature</span>
          <strong>{practice.stats.loggedTemperature}</strong>
        </Card>
        <Card className={`stat-card ${practice.stats.urgent > 0 ? 'urgent' : practice.stats.flagged > 0 ? 'attention' : ''}`}>
          <span className="hint">Flagged</span>
          <strong>
            {practice.stats.flagged}
            {practice.stats.urgent > 0 ? <small> · {practice.stats.urgent} urgent</small> : null}
          </strong>
        </Card>
      </div>

      {open.length > 0 ? (
        <Card>
          <h2>Open alerts</h2>
          <ul className="alerts">
            {open.map((a) => (
              <li key={a.id} className={`alert alert-${a.level}`}>
                <Icon name="alert" size={18} />
                <span className="row-main">
                  <strong>
                    <a href={`#/practice/patients/${encodeURIComponent(a.patientId)}`}>{a.patientName}</a> · {a.level}
                  </strong>
                  <span>{a.text}</span>
                  <span className="hint">{fmtAgo(a.at)}</span>
                </span>
                <button type="button" className="quiet small" onClick={() => void ack(a.id)}>
                  Acknowledge
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <Card className="table-card">
        {practice.patients.length === 0 ? (
          <Empty title="No patients yet">When your clinical manager invites patients and they create their accounts, they appear here.</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Doctor</th>
                  <th>Latest temp</th>
                  <th>Cycle</th>
                  <th>Adherence</th>
                  <th>Goals</th>
                  <th>Temps</th>
                  <th>Streak</th>
                  <th>Engagement</th>
                  <th>Flags</th>
                  <th>Last active</th>
                </tr>
              </thead>
              <tbody>
                {practice.patients.map((p) => {
                  const worst = p.flags.find((f) => f.level === 'urgent') ?? p.flags.find((f) => f.level === 'attention') ?? p.flags[0];
                  return (
                    <tr key={p.patientId} className={worst ? `row-${worst.level}` : ''}>
                      <td>
                        <a className="patient-cell" href={`#/practice/patients/${encodeURIComponent(p.patientId)}`}>
                          <Avatar name={p.name} size="sm" />
                          <span className="row-main">
                            <strong>{p.name}</strong>
                            <span className="hint">
                              {p.age ? `${p.age} · ` : ''}
                              {p.treatments.join(', ')}
                            </span>
                          </span>
                        </a>
                      </td>
                      <td>{p.doctorName}</td>
                      <td>
                        <strong className={p.latestTemperature && p.latestTemperature.class === 'fever' ? 'bad' : ''}>{p.latestTemperature ? `${fmtTemp(p.latestTemperature.celsius)}C` : '—'}</strong>
                        {p.latestTemperature ? <span className={`hint tone-${p.latestTemperature.class}`}> {p.latestTemperature.class}</span> : null}
                      </td>
                      <td>{p.cycle ? <span className={p.cycle.nadir ? 'warn' : ''}>{`C${p.cycle.number} d${p.cycle.day}`}</span> : <span className="hint">—</span>}</td>
                      <td className={p.adherencePercent >= 90 ? 'good' : p.adherencePercent >= 70 ? 'warn' : 'bad'}>{pct(p.adherencePercent)}</td>
                      <td>
                        <GoalsMet met={p.goalsMet} total={p.goalsTotal} />
                      </td>
                      <td>{p.temperaturesThisWeek}/7</td>
                      <td>{p.streak > 0 ? `${p.streak} d` : '—'}</td>
                      <td>
                        <span className="meter">
                          <i style={{ width: `${p.engagement}%` }} />
                        </span>{' '}
                        {p.engagement}
                      </td>
                      <td>{worst ? <span className={`badge badge-${worst.level === 'urgent' ? 'red' : worst.level === 'attention' ? 'gold' : 'soft'}`}>{worst.code.replace(/-/g, ' ')}</span> : <span className="hint">none</span>}</td>
                      <td className="hint">{p.lastActiveAt ? fmtAgo(p.lastActiveAt) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
